import React from 'react';
import './ProductItem.style.css';

import Comment from 'assets/svg/comment_icon.svg';
import Star from 'assets/svg/star_icon.svg';
import { discountedPrice, stringToMoneyFormat } from 'utils';

import Cart from 'assets/icon/cart_icon.svg';
import Heart from 'assets/icon/heart_icon.svg';

import dayjs from 'dayjs';

/**
 * 상품 아이콘
 * --
 */
const ProductIcon = ({
  text = '',
  color = '#111111',
  index = 0,
  total,
  style = {},
  screenSize,
}) => {
  return (
    <div
      className="product_icon"
      style={{
        background: color,
        //left: index ? `${80 * index - (screenSize <= 500 ? 38 : 15)}px` : 0,
        '--index': index,
        zIndex: total - index,
        ...style,
      }}
    >
      {text}
    </div>
  );
};

/**
 * 상품 이미지
 * --
 */
const ProductImage = ({
  src = null,
  style = {},
  title = '',
  hovered = true,
  buttons = [
    { render: () => <img src={Heart} alt="찜하기" />, onClick: () => {} },
    { render: () => <img src={Cart} alt="장바구니" />, onClick: () => {} },
  ],
  noImageStyle = {},
  isSoldOut = false,
  icons = [],
  screenSize,
}) => {
  return (
    <div
      className={`product_image ${!isSoldOut && hovered ? 'hovered' : ''}`}
      style={{ ...style, aspectRatio: '4 / 5' }} // ✅ 4:5 비율 적용
    >
      {icons.length
        ? icons.map((icon, index) => (
            <ProductIcon
              key={`icon-${icon}-${index}`}
              text={icon.label}
              color={icon.color}
              index={index}
              total={icons.length}
              screenSize={screenSize}
            />
          ))
        : null}

      {src ? (
        <img className="image" src={src} alt={title ?? '상품 이미지'} />
      ) : (
        <div className="no-image" style={{ ...noImageStyle }}></div>
      )}

      {isSoldOut ? <div className="product_sold_out">SOLD OUT</div> : null}

      <div className="product_image_hover_box">
        {buttons.map((_item, _index) => (
          <button key={`button-${_index}`} onClick={_item.onClick}>
            {_item.render()}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * 상품 태그
 * --
 */
const ProductTag = ({ label = '' }) => {
  return <span className="product_tag">{label}</span>;
};
const ProductTags = ({ tags = [], style = {} }) => {
  return (
    <div className="product_item_tags" style={{ ...style }}>
      {tags?.map((_item, _index) => (
        <ProductTag
          key={`tag-${_item.id}-${_index}-${dayjs().unix()}`}
          label={_item.label}
        />
      ))}
    </div>
  );
};

/**
 * 상품 타이틀
 * --
 */
const ProductTitle = ({ title = '', style = {}, maxLine = 1 }) => {
  return (
    <div
      className="product_item_title"
      style={{ WebkitLineClamp: maxLine, ...style }}
    >
      {title}
    </div>
  );
};

/**
 * 상품 리뷰
 * --
 */
const ProductReview = ({ comment, score, style = {} }) => {
  return (
    <ul className="product_item_review" style={{ ...style }}>
      {typeof comment === 'number' && comment >= 0 && (
        <li>
          <span>
            <img src={Comment} alt="리뷰 수" />
          </span>
          {comment}
        </li>
      )}
      {typeof score === 'number' && score >= 0 && (
        <li>
          <span>
            <img src={Star} alt="리뷰 점수" />
          </span>
          {parseFloat(score).toFixed(1)}
        </li>
      )}
    </ul>
  );
};

/**
 * 상품 가격
 * --
 */
const ProductPrice = ({
  prefix = null,
  price = 0,
  discount = 0,
  discountPrice = 0,
  type = '%',
  style = {},
  unit = '',
}) => {
  const calculatePrice = (price, discount, type) => {
    if (discount && discount > 0) return discountedPrice(price, discount, type);
    else return price;
  };

  return (
    <div
      className="product_item_price"
      style={{
        ...style,
        flexWrap: type === 'W' ? 'wrap-reverse' : 'wrap',
        flexDirection: type === 'W' ? 'row-reverse' : 'row',
      }}
    >
      {discount && discount > 0 ? (
        <span
          className="discount_price"
          style={{
            lineHeight: type === 'W' ? '130%' : 'unset',
          }}
        >
          {type === 'W'
            ? `(${stringToMoneyFormat(discount)}원 할인)`
            : `${discount}%`}
        </span>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'end',
          marginRight: 5,
        }}
      >
        {discount && discount > 0 ? (
          <strong>{stringToMoneyFormat(price)}</strong>
        ) : null}
        <React.Fragment>
          {prefix ? <small>{prefix}</small> : null}
          {stringToMoneyFormat(
            discountPrice
              ? discountPrice
              : calculatePrice(price, discount, type)
            //unit
          )}
          <div className="custom-unit">{unit}</div>
        </React.Fragment>
      </div>
    </div>
  );
};

const ProductItem = ({
  src = null,
  tags = [],
  brand = '',
  title = '',
  comment = null,
  score = null,
  prefix = null,
  price = 0,
  discount = 0,
  discountType = '%',
  discountPrice = 0,
  imageStyle = {},
  tagStyle = {},
  brandStyle = {},
  titleStyle = {},
  reviewStyle = {},
  priceStyle = {},
  maxLine = 1,
  hovered = true,
  buttons = [
    { render: () => <img src={Heart} alt="찜하기" />, onClick: () => {} },
    { render: () => <img src={Cart} alt="장바구니" />, onClick: () => {} },
  ],
  onClick,

  isSoldOut = false,
  icons = [],
}) => {
  /* ===== Route ===== */
  /* ===== Props ===== */
  /* ===== State ===== */
  const [screenSize, setScreenSize] = React.useState(document.body.clientWidth);

  /**
   * 스크린 사이즈 변화 감지
   * --
   */
  React.useEffect(() => {
    const call = (e) => {
      setScreenSize(document.body.clientWidth);
    };

    window.addEventListener('resize', call);

    return () => {
      window.removeEventListener('resize', call);
    };
  }, []);
  /* ===== Hooks ===== */
  /* ===== Function ===== */
  /* ===== Variable ===== */
  // const styles = {
  //   imageRatio: {
  //     width: '100%', // 부모 요소(Col)에 맞춰 자동 크기 조정
  //     height: 'auto',
  //     aspectRatio: '4 / 5', // ✅ 4:5 비율 유지
  //     objectFit: 'cover', // ✅ 이미지가 찌그러지지 않도록
  //     display: 'block', // ✅ inline-block 여백 제거
  //   },
  // };
  /* ===== Hooks ===== */
  /* ===== Render ===== */
  return (
    <div className="product_item_layout" onClick={onClick}>
      <ProductImage
        src={src}
        style={{ ...imageStyle }}
        title={title}
        hovered={hovered}
        buttons={buttons}
        isSoldOut={isSoldOut}
        icons={icons}
        screenSize={screenSize}
      />
      <div className="product_text">
        {brand && (
          <ProductTitle
            title={brand}
            style={{
              marginTop: screenSize >= 500 ? 32 : 15,
              fontSize: screenSize >= 500 ? 14 : 12,
              fontWeight: 700,
              ...brandStyle,
            }}
            maxLine={maxLine}
          />
        )}

        {title && (
          <ProductTitle
            title={title}
            style={{ marginTop: brand ? 4 : 0, ...titleStyle }}
            maxLine={maxLine}
          />
        )}

        {price && (
          <ProductPrice
            prefix={prefix}
            price={price}
            discount={discount}
            type={discountType}
            discountPrice={discountPrice}
            style={{ marginTop: title ? 8 : 0, ...priceStyle }}
            unit={'원'}
          />
        )}

        {(typeof comment === 'number' && comment >= 0) ||
        (typeof score === 'number' && score >= 0) ? (
          <ProductReview
            comment={comment}
            score={score}
            style={{ marginTop: price ? 8 : 0, ...reviewStyle }}
          />
        ) : null}

        {tags?.length ? (
          <ProductTags
            tags={tags}
            style={{
              marginTop:
                (typeof comment === 'number' && comment >= 0) ||
                (typeof score === 'number' && score >= 0)
                  ? 8
                  : 0,
              ...tagStyle,
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

ProductItem.Image = ProductImage;
ProductItem.Tags = ProductTags;
ProductItem.Title = ProductTitle;
ProductItem.Review = ProductReview;
ProductItem.Price = ProductPrice;

export default ProductItem;
